import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { PrimaryButton } from '@/components/PrimaryButton';
import { formatHistoryTimestamp, getNoteAuthorLabel } from '@/features/operations/labels';
import type { NoteEntry } from '@/features/operations/types';

export function NoteItem({
  note,
  onEdit,
  component = 'li',
}: {
  note: NoteEntry;
  onEdit: (noteId: string, body: string) => Promise<void>;
  component?: 'li' | 'div';
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!draft.trim()) {
      setError('La nota no puede quedar vacía.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onEdit(note.id, draft.trim());
      setIsEditing(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No pudimos guardar la nota.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack component={component} spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        {formatHistoryTimestamp(note.createdAt)} · {getNoteAuthorLabel(note)}
      </Typography>
      {isEditing ? (
        <Stack spacing={1}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Editar nota"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Stack direction="row" spacing={1}>
            <PrimaryButton size="small" disabled={isSaving} onClick={() => void handleSave()}>
              Guardar nota
            </PrimaryButton>
            <Button
              size="small"
              disabled={isSaving}
              onClick={() => {
                setDraft(note.body);
                setError(null);
                setIsEditing(false);
              }}
            >
              Cancelar
            </Button>
          </Stack>
        </Stack>
      ) : (
        <>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {note.body}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {note.editedAt ? (
              <Typography variant="caption" color="text.secondary">
                editado por {note.editedBy ? note.editedBy.fullName : 'PhoneTec'} ·{' '}
                {formatHistoryTimestamp(note.editedAt)}
              </Typography>
            ) : null}
            <Button
              size="small"
              sx={{ minWidth: 0, p: 0 }}
              aria-label={`Editar nota de ${getNoteAuthorLabel(note)}`}
              onClick={() => {
                setDraft(note.body);
                setIsEditing(true);
              }}
            >
              Editar
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}

export function NewNoteForm({ onAdd }: { onAdd: (body: string) => Promise<void> }) {
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!draft.trim()) {
      return;
    }

    setIsSaving(true);
    setAddError(null);

    try {
      await onAdd(draft.trim());
      setDraft('');
    } catch (cause) {
      setAddError(cause instanceof Error ? cause.message : 'No pudimos agregar la nota.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack spacing={1} sx={{ mt: 1.5 }}>
      {addError ? <Alert severity="error">{addError}</Alert> : null}
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Nueva nota"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <Box>
        <PrimaryButton
          size="small"
          disabled={isSaving || !draft.trim()}
          onClick={() => void handleAdd()}
        >
          Agregar nota
        </PrimaryButton>
      </Box>
    </Stack>
  );
}

export function NotesTimeline({
  notes,
  isLoading = false,
  error = null,
  onAdd,
  onEdit,
}: {
  notes: NoteEntry[];
  isLoading?: boolean;
  error?: string | null;
  onAdd: (body: string) => Promise<void>;
  onEdit: (noteId: string, body: string) => Promise<void>;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        NOTAS
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Cargando notas…
        </Typography>
      ) : notes.length ? (
        <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }}>
          {notes.map((note) => (
            <NoteItem key={note.id} note={note} onEdit={onEdit} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Todavía no hay notas.
        </Typography>
      )}
      <NewNoteForm onAdd={onAdd} />
    </Box>
  );
}
